import { css } from '@emotion/css';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { RootState } from '../../features';
import { actions } from '../../features/user';
import { sendEvent } from '../../lib/google-analytics';
import { ConfirmButton } from '../shared/buttons/ConfirmButton';
import { TitleHeader } from '../shared/TitleHeader';
import { LanguageCell } from './LanguageCell';

const languages = [
  { language: 'en-US', label: 'English (US)' },
  { language: 'en-GB', label: 'English (UK)' },
  { language: 'fr-FR', label: 'Français' },
  { language: 'fr-CA', label: 'Français canadien' },
  { language: 'de-DE', label: 'Deutsch' },
  { language: 'es-ES', label: 'Español' },
  { language: 'es-US', label: 'Español (US)' },
  { language: 'it-IT', label: 'Italiano' },
  { language: 'nl-NL', label: 'Nederlands' },
  { language: 'ru-RU', label: 'Pусский' },
  { language: 'ja-JP', label: '日本語' },
  { language: 'zh-TW', label: '繁體中文 ' },
  { language: 'zh-CN', label: '简体中文' },
  { language: 'ko-KR', label: '한국어 (대한민국)' }
];

export const LanguageSelect: React.FC = () => {
  const { t, i18n } = useTranslation(['settings', 'translation']);
  const { isLanguageUpdating } = useSelector((state: RootState) => state.user);
  const dispatch = useDispatch();
  const history = useHistory();
  const [checkedLanguage, setCheckedLanguage] = useState<string>(i18n.language);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (!isLanguageUpdating && confirmed) {
      // Workaround: ホーム画面に戻り，historyスタックの最初の状態になりたい。
      history.go(-2);
    }
  }, [confirmed, history, isLanguageUpdating]);

  const handleLanguageClick = useCallback(
    (language: string) => () => {
      setCheckedLanguage(language);
    },
    [setCheckedLanguage]
  );

  const handleConfirmClick = useCallback(() => {
    dispatch(actions.updateLanguage(checkedLanguage));
    setConfirmed(true);
    sendEvent('settings_complete_select_language', 'click');
  }, [dispatch, checkedLanguage, setConfirmed]);

  return (
    <main
      className={css`
        display: flex;
        flex-direction: column;
        background: #f5f1e3;
        font-size: 16px;
        color: #4d3906;
        width: 100%;
        height: 100%;

        .body {
          flex: 1 1 auto;
          display: flex;
          flex-direction: column;
          width: 100%;
          max-width: 480px;
          overflow: hidden;
          margin: 0 auto;
        }
        .desc {
          font-size: 14px;
          margin: 24px 32px 12px;
        }
        .items {
          overflow-y: scroll;
          pointer-events: ${isLanguageUpdating ? 'none' : 'auto'};
          padding: 0 0 40px;
        }
        .button {
          flex: 0 0 auto;
          width: 100%;
          max-width: 480px;
          margin: 0 auto;
          padding: 12px 16px 24px;
        }
        .button.loading {
          pointer-events: none;
          opacity: 0.5;
        }
      `}
    >
      <TitleHeader title={t('language')} color="#dbcca7" backLabel={t('aria_back_to_settings')} />
      <div className="body">
        <p className="desc">{t('select_language')}</p>
        <ul className="items">
          {languages.map(item => (
            <LanguageCell
              key={item.language}
              label={item.label}
              language={item.language}
              onClick={handleLanguageClick(item.language)}
              isChecked={checkedLanguage === item.language}
              isSelected={i18n.language === item.language}
            />
          ))}
        </ul>
      </div>
      <div className={`button ${isLanguageUpdating ? 'loading' : ''}`}>
        <ConfirmButton
          onClick={handleConfirmClick}
          disabled={checkedLanguage === i18n.language}
          loading={isLanguageUpdating}
        >
          {t('translation:dialog_button_ok')}
        </ConfirmButton>
      </div>
    </main>
  );
};
